/**
 * Live sessions, and revoking them (`core-api` §4.4).
 *
 * Sessions are rows, so revocation is refused at the very next request rather
 * than at the next expiry. That is the whole reason this screen is worth having
 * on a phone: a tablet left at a friend's house stops working in seconds.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { SessionOut } from "@/api/schema";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

const when = (value: string | null) => (value ? new Date(value).toLocaleString("fr-FR") : "—");

export function SessionsScreen() {
  const [sessions, setSessions] = useState<SessionOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSessions(await call("listSessions"));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : "Chargement impossible.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!sessions) return <Loading />;

  const live = sessions.filter((session) => !session.revoked_at);

  return (
    <Screen title="Sessions ouvertes">
      <Card>
        {live.length === 0 ? (
          <Empty>Aucune session ouverte.</Empty>
        ) : (
          <ul className="list">
            {live.map((session) => (
              <li key={session.id} className="list__item">
                <div>
                  <strong>{session.device_id ? "Appareil enrôlé" : "Navigateur"}</strong>
                  <p className="muted small">
                    ouverte {when(session.created_at)} · vue {when(session.last_seen_at)} · expire{" "}
                    {when(session.expires_at)}
                  </p>
                </div>
                <Button
                  variant="danger"
                  onClick={() => void call("revokeSession", { params: { session_id: session.id } }).then(load)}
                >
                  Fermer
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Screen>
  );
}
